/**
 * Deterministic pseudonym generation for demo mode.
 *
 * Every generator maps a real value to a stable fake one: the same input always produces
 * the same output, within and across page loads and across all API clients. That keeps
 * relationships coherent everywhere a value appears (e.g. a charging session's `depotName`
 * still matches the site it belongs to on the Sites page). Nothing here is reversible
 * security — it is presentation-layer masking for live demos only.
 */

/** 32-bit FNV-1a hash. Stable and fast, with distribution good enough for list picking. */
export function fnv1a(input: string, seed = 0x811c9dc5): number {
  let hash = seed >>> 0
  for (let i = 0; i < input.length; i += 1) {
    hash ^= input.charCodeAt(i)
    hash = Math.imul(hash, 0x01000193) >>> 0
  }
  return hash >>> 0
}

/**
 * Derives three independent hash values for the same (category, value) pair, so multi-part
 * pseudonyms ("first last", "adjective noun type") don't correlate their parts.
 */
function hashes(category: string, value: string): [number, number, number] {
  const h1 = fnv1a(`${category}|${value}`)
  const h2 = fnv1a(value, h1)
  const h3 = fnv1a(value, h2)
  return [h1, h2, h3]
}

// The `?? ''` can't fire (every list is non-empty and the index is always in range) but
// keeps the return type `string` under noUncheckedIndexedAccess.
const pick = (list: readonly string[], hash: number): string => list[hash % list.length] ?? ''
const pad = (value: number, width: number) => String(value).padStart(width, '0')

const ORG_PREFIXES = [
  'Northwind',
  'Bluepeak',
  'Ironwood',
  'Silverline',
  'Crestway',
  'Harborview',
  'Stonebridge',
  'Clearfield',
  'Westbrook',
  'Summit',
  'Redstone',
  'Lakeshore',
  'Pinnacle',
  'Copperleaf',
  'Brightpath',
  'Meridian',
  'Oakline',
  'Fairwind',
  'Granite',
  'Riverbend',
  'Skyline',
  'Cobalt',
  'Amberfield'
] as const

const ORG_SUFFIXES = [
  'Logistics',
  'Transit',
  'Fleet Services',
  'Distribution',
  'Freight Lines',
  'Delivery Co',
  'Transport Group',
  'Mobility',
  'Carriers',
  'Hauling',
  'Express',
  'Supply Co'
] as const

const PLACE_ADJECTIVES = [
  'Cedar',
  'Willow',
  'Maple',
  'Juniper',
  'Aspen',
  'Birch',
  'Alder',
  'Laurel',
  'Sycamore',
  'Magnolia',
  'Cypress',
  'Hawthorn',
  'Poplar',
  'Hickory',
  'Spruce',
  'Elm',
  'Rowan',
  'Linden',
  'Chestnut',
  'Sequoia'
] as const

const PLACE_NOUNS = [
  'Ridge',
  'Grove',
  'Hollow',
  'Meadow',
  'Crossing',
  'Point',
  'Landing',
  'Terrace',
  'Valley',
  'Bluff',
  'Springs',
  'Bend',
  'Gate',
  'Summit',
  'Glen',
  'Harbor',
  'Prairie',
  'Junction'
] as const

const SITE_TYPES = ['Depot', 'Yard', 'Hub', 'Terminal', 'Station'] as const

const FIRST_NAMES = [
  'Alex',
  'Jordan',
  'Taylor',
  'Morgan',
  'Casey',
  'Riley',
  'Avery',
  'Quinn',
  'Cameron',
  'Rowan',
  'Emerson',
  'Finley',
  'Hayden',
  'Jules',
  'Kendall',
  'Logan',
  'Marlow',
  'Noel',
  'Parker',
  'Reese',
  'Sage',
  'Skyler',
  'Tatum',
  'Wren'
] as const

const LAST_NAMES = [
  'Ashford',
  'Barlow',
  'Calloway',
  'Dorsey',
  'Ellison',
  'Fairbanks',
  'Granger',
  'Holloway',
  'Ingram',
  'Jennings',
  'Kirkland',
  'Lockhart',
  'Mercer',
  'Norwood',
  'Oakley',
  'Pemberton',
  'Quimby',
  'Radcliffe',
  'Sterling',
  'Thatcher',
  'Underhill',
  'Vance',
  'Whitfield',
  'Yardley'
] as const

const STREET_NAMES = [
  'Cedar',
  'Lakeview',
  'Sunset',
  'Prospect',
  'Franklin',
  'Jefferson',
  'Sycamore',
  'Ridgeline',
  'Meadowbrook',
  'Harbor',
  'Willow',
  'Foxglove',
  'Juniper',
  'Bayside',
  'Granite'
] as const

const STREET_TYPES = ['St', 'Ave', 'Blvd', 'Rd', 'Dr', 'Way', 'Pkwy'] as const

const CITY_NAMES = [
  'Fairview',
  'Riverton',
  'Brookdale',
  'Lakemont',
  'Cedarburg',
  'Milbrook',
  'Ashvale',
  'Norwich Falls',
  'Winfield',
  'Harper Springs',
  'Eastvale',
  'Clarkston',
  'Maplewood',
  'Greenhollow',
  'Stonehaven',
  'Westport'
] as const

/** VIN character set (I, O, and Q are never used in real VINs). */
const VIN_CHARS = 'ABCDEFGHJKLMNPRSTUVWXYZ0123456789'
const BASE36 = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ'
const HEX = '0123456789ABCDEF'

function fromCharset(category: string, value: string, charset: string, length: number): string {
  let out = ''
  let h = fnv1a(`${category}|${value}`)
  for (let i = 0; i < length; i += 1) {
    out += charset[h % charset.length]
    h = fnv1a(value, h)
  }
  return out
}

export function organizationName(value: string): string {
  const [a, b] = hashes('org', value)
  return `${pick(ORG_PREFIXES, a)} ${pick(ORG_SUFFIXES, b)}`
}

export function siteName(value: string): string {
  const [a, b, c] = hashes('site', value)
  return `${pick(PLACE_ADJECTIVES, a)} ${pick(PLACE_NOUNS, b)} ${pick(SITE_TYPES, c)}`
}

export function firstName(value: string): string {
  const [a] = hashes('person', value)
  return pick(FIRST_NAMES, a)
}

export function lastName(value: string): string {
  const [, b] = hashes('person', value)
  return pick(LAST_NAMES, b)
}

export function personName(value: string): string {
  return `${firstName(value)} ${lastName(value)}`
}

/**
 * Neutral two-word label for `name` fields whose entity type can't be inferred
 * (sites, customers, saved views, and other user-authored labels).
 */
export function genericLabel(value: string): string {
  const [a, b] = hashes('label', value)
  return `${pick(PLACE_ADJECTIVES, a)} ${pick(PLACE_NOUNS, b)}`
}

export function vehicleName(value: string): string {
  const [a] = hashes('vehicle', value)
  return `Unit ${1000 + (a % 9000)}`
}

export function chargerName(value: string): string {
  const [a, b] = hashes('charger', value)
  return `Charger ${String.fromCharCode(65 + (a % 26))}${pad(b % 100, 2)}`
}

export function emailAddress(value: string): string {
  const [a] = hashes('email', value)
  const person = personName(value).toLowerCase().replace(' ', '.')
  return `${person}${a % 100}@demo.example.com`
}

export function phoneNumber(value: string): string {
  const [a, b] = hashes('phone', value)
  return `(555) ${pad(100 + (a % 900), 3)}-${pad(b % 10000, 4)}`
}

export function streetAddress(value: string): string {
  const [a, b, c] = hashes('street', value)
  return `${100 + (a % 9800)} ${pick(STREET_NAMES, b)} ${pick(STREET_TYPES, c)}`
}

export function cityName(value: string): string {
  const [a] = hashes('city', value)
  return pick(CITY_NAMES, a)
}

export function postalCode(value: string): string {
  const [a] = hashes('postal', value)
  return pad(10000 + (a % 89999), 5)
}

export function vin(value: string): string {
  return fromCharset('vin', value, VIN_CHARS, 17)
}

/** Opaque replacement for serial numbers, external IDs, RFID/OCPP tags, SIM identifiers, etc. */
export function token(value: string): string {
  return `DEMO-${fromCharset('token', value, BASE36, 8)}`
}

export function macAddress(value: string): string {
  const raw = fromCharset('mac', value, HEX, 12)
  return raw.replace(/(..)(?!$)/g, '$1:')
}

export function url(value: string): string {
  return `https://demo.example.com/${fromCharset('url', value, BASE36, 6).toLowerCase()}`
}

/** Fake but distinct-per-input domain for SSO/email-domain fields. */
export function domainName(value: string): string {
  return `demo-${fromCharset('domain', value, BASE36, 6).toLowerCase()}.example.com`
}

const DESCRIPTION_PHRASES = [
  'Sample description',
  'Example description',
  'Demo details',
  'Placeholder details',
  'Sample entry',
  'Example note',
  'Demo record',
  'Sample details'
] as const

/**
 * Neutral deterministic replacement for free-text fields (descriptions, notes, memos,
 * terms, labels). Free text can contain arbitrary customer identifiers, so rather than
 * try to preserve meaning we swap in an obviously-placeholder phrase.
 */
export function descriptionText(value: string): string {
  const [a] = hashes('description', value)
  return pick(DESCRIPTION_PHRASES, a)
}
